class AveyGlobalShadePicker {
  constructor() {
    this.dialog = document.getElementById('avey-global-shade-picker');
    this.optionsContainer = this.dialog?.querySelector('[data-global-shade-options]');
    this.activeSelect = null;
    this.activeTrigger = null;
    this.selectionTimer = null;
    this.bundlePrimaryShades = new WeakMap();

    if (!this.dialog || !this.optionsContainer) return;

    this.enhanceAll();
    document.addEventListener('click', this.onClick, true);
    // Fill the bundle fields before its own change handler computes the items.
    document.addEventListener('change', this.onChange, true);
    document.addEventListener('avey:cart:updated', this.onCartUpdated);
    document.addEventListener('avey:overlay:close', this.onOverlayClose);
    this.dialog.addEventListener('avey:overlay:request-close', this.clearSelectionTimer);
  }

  enhanceAll(root = document) {
    root.querySelectorAll('[data-shade-select]:not(.is-enhanced)').forEach((container) => {
      const select = container.querySelector('select');
      const visual = container.querySelector('[data-shade-select-visual]');
      if (!(select instanceof HTMLSelectElement) || !(visual instanceof HTMLElement)) return;

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'avey-shade-select__trigger';
      trigger.dataset.globalShadeOpen = '';
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', this.dialog.id);
      trigger.setAttribute('aria-label', select.getAttribute('aria-label') || this.getLabel(select));

      const value = document.createElement('span');
      value.className = 'avey-shade-select__value';
      value.dataset.shadeSelectValue = '';

      const chevron = document.createElement('span');
      chevron.className = 'avey-shade-select__chevron';
      chevron.setAttribute('aria-hidden', 'true');

      trigger.append(visual, value, chevron);
      select.classList.add('avey-shade-select__native');
      select.setAttribute('tabindex', '-1');
      select.setAttribute('aria-hidden', 'true');
      container.insertBefore(trigger, select);
      container.classList.add('is-enhanced');
      this.syncTrigger(select);
    });
  }

  getLabel(select) {
    if (select.id) {
      const label = document.querySelector(`label[for="${CSS.escape(select.id)}"]`);
      if (label?.textContent.trim()) return label.textContent.trim();
    }
    const hiddenLabel = select.closest('[data-shade-select]')
      ?.querySelector('.visually-hidden')?.textContent.trim();
    if (hiddenLabel) return hiddenLabel;
    if (document.documentElement.lang.startsWith('es')) return 'Elegir tono';
    if (document.documentElement.lang.startsWith('en')) return 'Choose shade';
    return 'Choisir la teinte';
  }

  syncTrigger(select) {
    const container = select.closest('[data-shade-select]');
    const option = select.selectedOptions[0];
    const trigger = container?.querySelector('[data-global-shade-open]');
    const value = trigger?.querySelector('[data-shade-select-value]');
    const visual = trigger?.querySelector('[data-shade-select-visual]');
    if (!option || !trigger || !value || !visual) return;

    value.textContent = option.textContent.trim();
    const image = option.dataset.shadeImage || '';
    visual.classList.toggle('avey-shade-select__visual--fallback', !image);
    visual.style.setProperty('--avey-shade-image', image ? `url("${image}")` : 'none');
  }

  onClick = (event) => {
    const trigger = event.target.closest('[data-global-shade-open]');
    if (trigger) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(trigger);
      return;
    }

    const option = event.target.closest('[data-global-shade-option]');
    if (option) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.select(option);
      return;
    }

  };

  onCartUpdated = () => {
    requestAnimationFrame(() => this.enhanceAll());
  };

  onChange = (event) => {
    this.syncBundleShades(event.target);
    const select = event.target.closest?.('[data-shade-select] select');
    if (select instanceof HTMLSelectElement) this.syncTrigger(select);
  };

  syncBundleShades(field) {
    const shadeChanged = field.matches?.('[data-bundle-shade]');
    const lotChanged = field.matches?.('[data-bundle-radio]') && field.checked;
    if (!shadeChanged && !lotChanged) return;
    const bundle = field.closest('avey-bundle-selector');
    const card = field.closest('[data-bundle-card]');
    if (!bundle || !card) return;
    const fields = [...card.querySelectorAll('[data-bundle-shade]')];
    // Later bottles may keep their own shade until the first shade changes again.
    if (shadeChanged && field !== fields[0]) return;
    const value = shadeChanged ? field.value : this.bundlePrimaryShades.get(bundle);
    if (!value || !bundle.closest('avey-product')?.variantData?.some(
      (variant) => String(variant.id) === value && variant.available,
    )) return;
    if (!fields[0]?.options || ![...fields[0].options].some(
      (option) => option.value === value && !option.disabled,
    )) return;

    if (shadeChanged) this.bundlePrimaryShades.set(bundle, value);
    const targets = shadeChanged ? bundle.querySelectorAll('[data-bundle-shade]') : fields;
    targets.forEach((select) => {
      if (select.value === value || ![...select.options].some(
        (option) => option.value === value && !option.disabled,
      )) return;
      select.value = value;
      this.syncTrigger(select);
    });
  }

  onOverlayClose = (event) => {
    if (event.detail?.overlay !== this.dialog) return;
    this.reset();
  };

  open(trigger) {
    const select = trigger.closest('[data-shade-select]')?.querySelector('select');
    if (!(select instanceof HTMLSelectElement)) return;

    this.activeSelect = select;
    this.activeTrigger = trigger;
    this.renderOptions(select);
    trigger.setAttribute('aria-expanded', 'true');

    window.AveyOverlayManager?.open(this.dialog, trigger);
  }

  renderOptions(select) {
    const fragment = document.createDocumentFragment();
    [...select.options].forEach((selectOption) => {
      if (!selectOption.value && selectOption.disabled) return;
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'avey-global-shade-picker__option';
      option.dataset.globalShadeOption = '';
      option.dataset.value = selectOption.value;
      option.disabled = selectOption.disabled;
      option.setAttribute('aria-pressed', String(selectOption.selected));

      const visual = document.createElement('span');
      visual.className = 'avey-shade-select__visual';
      visual.setAttribute('aria-hidden', 'true');
      const image = selectOption.dataset.shadeImage || '';
      visual.classList.toggle('avey-shade-select__visual--fallback', !image);
      visual.style.setProperty('--avey-shade-image', image ? `url("${image}")` : 'none');

      const label = document.createElement('span');
      label.textContent = selectOption.textContent.trim();
      option.append(visual, label);
      fragment.append(option);
    });
    this.optionsContainer.replaceChildren(fragment);
  }

  select(option) {
    if (!this.activeSelect || !(option instanceof HTMLButtonElement)) return;

    this.optionsContainer.querySelectorAll('[data-global-shade-option]').forEach((shadeOption) => {
      shadeOption.setAttribute('aria-pressed', String(shadeOption === option));
      shadeOption.classList.toggle('is-confirmed', shadeOption === option);
    });

    this.activeSelect.value = option.dataset.value;
    this.activeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncTrigger(this.activeSelect);

    // Leave the confirmed outline visible briefly before closing. The same
    // lightweight picker serves bundles, collections and cart suggestions.
    this.clearSelectionTimer();
    this.selectionTimer = window.setTimeout(() => this.close(), 220);
  }

  close() {
    this.clearSelectionTimer();
    if (!this.dialog.open) return;
    return window.AveyOverlayManager?.close(this.dialog);
  }

  clearSelectionTimer = () => {
    if (this.selectionTimer) window.clearTimeout(this.selectionTimer);
    this.selectionTimer = null;
  };

  reset() {
    this.clearSelectionTimer();
    this.activeTrigger?.setAttribute('aria-expanded', 'false');
    this.optionsContainer.replaceChildren();
    this.activeSelect = null;
    this.activeTrigger = null;
  }
}

window.AveyGlobalShadePicker = new AveyGlobalShadePicker();
